import { usePrevious } from '@sequencehq/utils'
import { dequal } from 'dequal'
import { useCallback, useEffect, useState } from 'react'

export type RecipientType = 'SIGNER' | 'CC'

export type RecipientsValue = {
  contacts: string[]
  ccContacts: string[]
}

type RecipientsSelection = {
  selectedContacts: string[]
  contactTypes: Record<string, RecipientType>
}

export const useRecipientsSelection = (value: RecipientsValue) => {
  const [selection, setSelection] = useState<RecipientsSelection>(() =>
    valueToSelection(value)
  )
  const previousValue = usePrevious(value)

  useEffect(() => {
    if (previousValue === undefined || dequal(previousValue, value)) {
      return
    }

    const externalSelection = valueToSelection(value)
    setSelection(currentSelection => ({
      selectedContacts: externalSelection.selectedContacts,
      contactTypes: {
        ...currentSelection.contactTypes,
        ...externalSelection.contactTypes
      }
    }))
  }, [previousValue, value])

  const setSelected = useCallback(
    (selectedContacts: string[]): RecipientsValue => {
      const nextSelection = { ...selection, selectedContacts }
      setSelection(nextSelection)
      return selectionToValue(nextSelection)
    },
    [selection]
  )

  const setType = useCallback(
    (contactId: string, type: RecipientType): RecipientsValue => {
      const nextSelection = {
        ...selection,
        contactTypes: {
          ...selection.contactTypes,
          [contactId]: type
        }
      }
      setSelection(nextSelection)
      return selectionToValue(nextSelection)
    },
    [selection]
  )

  const toValue = useCallback(
    (): RecipientsValue => selectionToValue(selection),
    [selection]
  )

  return {
    selectedContacts: selection.selectedContacts,
    contactTypes: selection.contactTypes,
    setSelected,
    setType,
    toValue
  }
}

const valueToSelection = (value: RecipientsValue): RecipientsSelection => ({
  selectedContacts: Array.from(
    new Set([...value.contacts, ...value.ccContacts])
  ),
  contactTypes: {
    ...Object.fromEntries(
      value.ccContacts.map(contactId => [contactId, 'CC' as const])
    ),
    ...Object.fromEntries(
      value.contacts.map(contactId => [contactId, 'SIGNER' as const])
    )
  }
})

const selectionToValue = ({
  selectedContacts,
  contactTypes
}: RecipientsSelection): RecipientsValue => ({
  contacts: selectedContacts.filter(
    contactId => (contactTypes[contactId] ?? 'SIGNER') === 'SIGNER'
  ),
  ccContacts: selectedContacts.filter(
    contactId => contactTypes[contactId] === 'CC'
  )
})
